import React from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { useBetty } from '../../context/BettyContext';
import { useAudio } from '../../context/AudioContext';
import { BettyChat } from './BettyChat';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// Hoisted animation variants to prevent recreation on every render
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const chatVariants = {
  hidden: {
    y: '100%',
    opacity: 0,
    scale: 0.9
  },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300
    }
  },
  exit: {
    y: '100%',
    opacity: 0,
    scale: 0.9,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 300
    }
  }
};

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 9999;
  pointer-events: auto; /* Allow close on click outside chat */
`;

const ChatContainer = styled(motion.div)<{ $hasAudio?: boolean }>`
  position: fixed;
  bottom: ${props => props.$hasAudio ? '92px' : '0'}; /* Move above player when audio is playing */
  right: 0;
  width: 500px;
  height: ${props => props.$hasAudio ? 'calc(80vh - 92px)' : '80vh'}; /* Adjust height when player is visible */
  max-height: ${props => props.$hasAudio ? 'calc(800px - 92px)' : '800px'};
  background: 
    linear-gradient(135deg, 
      rgba(255, 248, 240, 0.95) 0%, 
      rgba(255, 240, 230, 0.9) 50%,
      rgba(255, 235, 220, 0.85) 100%
    );
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 182, 193, 0.3);
  border-bottom: none;
  overflow: hidden;
  z-index: 10000;
  transition: all 0.3s ease; /* Smooth transition when player appears/disappears */

  @media (max-width: 768px) {
    width: 100vw;
    height: ${props => props.$hasAudio ? 'calc(100vh - 104px)' : '100vh'}; /* Extra space on mobile */
    border-radius: 0;
    bottom: ${props => props.$hasAudio ? '104px' : '0'};
    right: 0;
    left: 0;
  }
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(255, 182, 193, 0.4);
  backdrop-filter: blur(10px);
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff69b4, #ffb6c1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(255, 105, 180, 0.3);
  color: white;
  font-weight: bold;
`;

const HeaderText = styled.div`
  h3 {
    margin: 0;
    color: #8b4513;
    font-size: 1rem;
    font-family: 'Georgia', serif;
    line-height: 1.1;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: rgba(139, 69, 19, 0.8);
    font-size: 0.75rem;
    font-style: italic;
    line-height: 1.1;
  }
`;

const ActionButton = styled.button<{ $isActive?: boolean }>`
  background: ${props => props.$isActive ? 'rgba(255, 182, 193, 0.3)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => props.$isActive ? 'rgba(255, 182, 193, 0.6)' : 'rgba(255, 182, 193, 0.3)'};
  color: #8b4513;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;

  &:hover {
    background: rgba(255, 182, 193, 0.4);
    color: #8b4513;
    border-color: rgba(255, 182, 193, 0.7);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(255, 182, 193, 0.3);
  }
`;

const CloseButton = styled(ActionButton)`
  font-size: 1.5rem;
`;

const BettyBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('/images/betty.png');
  background-size: 60% auto;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.3;
  pointer-events: none;
  z-index: -1;
`;

const ChatContent = styled.div`
  height: calc(100% - 50px);
  position: relative;
  display: flex;
  flex-direction: column;
`;

const MinimizedChatContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .betty-chat-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .betty-header {
    display: none; /* Hide the full header since we have our own */
  }

  .betty-messages-container {
    flex: 1;
    overflow-y: auto;
  }

  .betty-input-container {
    flex-shrink: 0;
    margin-top: auto;
  }
`;

export const BettyChatOverlay: React.FC<Props> = ({ isOpen, onClose }) => {
  const { searchResults, isSidebarOpen, openSidebar, closeSidebar, messages, clearHistory } = useBetty();
  const { currentTrack } = useAudio();
  
  // Check if audio player is visible (when there's a current track)
  const hasAudioPlayer = !!currentTrack;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            key="betty-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={onClose}
          />
          <ChatContainer
            key="betty-chat-container"
            $hasAudio={hasAudioPlayer}
            variants={chatVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <BettyBackground />
            <ChatHeader>
              <HeaderInfo>
                <Avatar>🎵</Avatar>
                <HeaderText>
                  <h3>Betty</h3>
                  <p>Music Licensing Assistant</p>
                </HeaderText>
              </HeaderInfo>
              <HeaderActions>
                {searchResults.length > 0 && (
                  <ActionButton
                    $isActive={isSidebarOpen}
                    onClick={() => isSidebarOpen ? closeSidebar() : openSidebar()}
                    title={`${isSidebarOpen ? 'Hide' : 'Show'} search results (${searchResults.length})`}
                  >
                    📋
                  </ActionButton>
                )}
                {messages.length > 0 && (
                  <ActionButton
                    onClick={clearHistory}
                    title="Clear chat history"
                  >
                    🗑️
                  </ActionButton>
                )}
                <CloseButton onClick={onClose}>
                  ×
                </CloseButton>
              </HeaderActions>
            </ChatHeader>
            
            <ChatContent>
              <MinimizedChatContainer>
                <BettyChat isOverlay={true} />
              </MinimizedChatContainer>
            </ChatContent>
          </ChatContainer>
        </>
      )}
    </AnimatePresence>
  );
};